import { PayloadAction } from '@reduxjs/toolkit';
import { ImageContext, ImageDocumentStoreItem } from '@v2/components/imageHandling/types/types';
import { ImageHandlingState, isImageContextHasScopeId } from '@reducers/v2/imageHandlingV2/imageHandlingV2.slice';
import { getImageContextKey } from '@v2/components/imageHandling/helpers';
import { TTL_FOR_IMAGE } from '@v2/components/imageHandling/constants';
import { DOCUMENT_HIERARCHY_CONFIG } from '@components/imageHandling/common';

interface AddOrUpdateImageDocumentActionPayload {
  imageContext: ImageContext;
  document: ImageDocumentStoreItem;
  replacementDocumentId?: string;
  saveToEnd?: boolean;
}

export const addOrUpdateImageDocumentAction = (state: ImageHandlingState, action: PayloadAction<AddOrUpdateImageDocumentActionPayload>) => {
  const { imageContext, document, replacementDocumentId, saveToEnd = false } = action.payload;
  const key = getImageContextKey(imageContext);

  const shouldAddScopeNameToDocument = isImageContextHasScopeId(imageContext);
  const scopeName = shouldAddScopeNameToDocument
    ? Object.values(DOCUMENT_HIERARCHY_CONFIG).find((c) => c.scopeId === imageContext?.scopeId)?.scopeName
    : null;

  const documentWithScopeNameAndId = {
    ...document,
    ...(shouldAddScopeNameToDocument && scopeName ? { scopeName, scopeId: imageContext.scopeId } : {}),
  };

  const documentId = documentWithScopeNameAndId.documentId;

  // Store the document in the dictionary
  state.imagesDocumentsDict[documentId] = documentWithScopeNameAndId;

  if (!state.imagesDocumentsContexts[key]) {
    state.imagesDocumentsContexts[key] = {
      documentIds: [documentId],
      ttl: Date.now() + TTL_FOR_IMAGE,
      loading: false,
      error: null,
    };
  } else {
    const documentIds = state.imagesDocumentsContexts[key].documentIds;

    const existingIndex = documentIds.findIndex((id) => id === documentId);

    if (replacementDocumentId) {
      state.imagesDocumentsContexts[key].documentIds = documentIds.map((id) => (id === replacementDocumentId ? document.documentId : id));
      if (state.imagesDocumentsDict[replacementDocumentId]) {
        delete state.imagesDocumentsDict[replacementDocumentId];
      }
    }

    if (existingIndex !== -1) {
      // Document already exists in context, just update the dictionary (already done above)
      // No need to modify the documentIds array
    } else {
      // Add new document ID to the context - create a new array to ensure selector updates
      if (saveToEnd) {
        state.imagesDocumentsContexts[key].documentIds = [...documentIds, documentId];
      } else {
        state.imagesDocumentsContexts[key].documentIds = [documentId, ...documentIds];
      }
    }
  }
};
